import React, { useState, useEffect, useRef, useContext } from "react";
import {
  getOrgChartData,
  getUserById,
  getUserContactQr,
  getAllGroups,
  createRole,
} from "../../api/api";
import { getAPIURL } from "../../api/config";
import Select from "react-select";
import { Modal, message, Spin } from "antd";
import "./OrgChartNew.css";
import { UserContext } from "../../contexts/CurrentUserContext";
import { selectStyles } from "../../utils/selectStyles";

const TreeNode = ({
  node,
  onEmployeeClick,
  expandedNodes,
  setExpandedNodes,
  selectedEmployee,
  allNodes,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isSelected = selectedEmployee === node.id;

  // Находим заместителей для текущего отделения
  const getDeputies = () => {
    if (!node.id.startsWith("dept_")) return { left: [], right: [] };
    const departmentId = node.id.split("dept_")[1];
    const deputies = allNodes.filter(
      (n) =>
        n.tags?.includes("deputy") && n.departmentId === parseInt(departmentId)
    );

    // Разделяем заместителей на две группы
    const midPoint = Math.ceil(deputies.length / 2);
    return {
      left: deputies.slice(0, midPoint),
      right: deputies.slice(midPoint),
    };
  };

  const deputies = getDeputies();
  const hasDeputies = deputies.left.length > 0 || deputies.right.length > 0;

  const toggleExpand = (e) => {
    e.stopPropagation();
    const newExpandedNodes = new Set(expandedNodes);
    if (isExpanded) {
      newExpandedNodes.delete(node.id);
    } else {
      newExpandedNodes.add(node.id);
    }
    setExpandedNodes(newExpandedNodes);
  };

  // Проверяем, являются ли все дочерние элементы конечными узлами (сотрудниками)
  const areAllChildrenEndNodes = (children) => {
    return children.every(
      (child) => !child.children || child.children.length === 0
    );
  };

  return (
    <div className="orgchart-new__tree-node">
      <div className="orgchart-new__level-container">
        {/* Заместители над руководителем - теперь показываются всегда */}
        {hasDeputies && (
          <div className="orgchart-new__deputies-container">
            {[...deputies.left, ...deputies.right].map((deputy) => (
              <div
                key={deputy.id}
                data-employee-id={deputy.id}
                className={`orgchart-new__node orgchart-new__node--deputy ${
                  selectedEmployee === deputy.id
                    ? "orgchart-new__node--selected"
                    : ""
                }`}
                onClick={() => onEmployeeClick(deputy)}
              >
                <div className="orgchart-new__node-content">
                  <div className="orgchart-new__position">{deputy.title}</div>
                  <div className="orgchart-new__avatar">
                    <img
                      src={
                        deputy.img || "/assets/images/placeholder-avatar.png"
                      }
                      alt={deputy.name}
                    />
                  </div>
                  <div className="orgchart-new__name">{deputy.name}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Основной узел */}
        <div
          data-employee-id={node.id}
          className={`orgchart-new__node ${
            isSelected ? "orgchart-new__node--selected" : ""
          } ${
            node.tags?.includes("director")
              ? "orgchart-new__node--director"
              : ""
          }`}
        >
          <div
            className="orgchart-new__node-content"
            onClick={() => onEmployeeClick(node)}
          >
            <div className="orgchart-new__position">{node.title}</div>
            <div className="orgchart-new__avatar">
              <img
                src={node.img || "/assets/images/placeholder-avatar.png"}
                alt={node.name}
              />
            </div>
            <div className="orgchart-new__name">{node.name}</div>
          </div>
          {(hasChildren || hasDeputies) && (
            <button
              className={`orgchart-new__expand-btn ${
                isExpanded ? "expanded" : ""
              }`}
              onClick={toggleExpand}
            >
              {isExpanded ? "▼" : "▶"}
            </button>
          )}
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div
          className={`orgchart-new__children ${
            areAllChildrenEndNodes(node.children) ? "horizontal" : ""
          }`}
        >
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              onEmployeeClick={onEmployeeClick}
              expandedNodes={expandedNodes}
              setExpandedNodes={setExpandedNodes}
              selectedEmployee={selectedEmployee}
              allNodes={allNodes}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const EmployeeModal = ({ visible, employee, onClose }) => {
  const [qrSrc, setQrSrc] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState(null);
  const qrObjectUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const revokeQrUrl = () => {
      if (qrObjectUrlRef.current) {
        URL.revokeObjectURL(qrObjectUrlRef.current);
        qrObjectUrlRef.current = null;
      }
    };

    const loadQr = async () => {
      revokeQrUrl();
      setQrSrc(null);
      setQrError(null);

      if (!visible || !employee?.id) {
        setQrLoading(false);
        return;
      }

      setQrLoading(true);
      try {
        const src = await getUserContactQr(employee.id);
        if (cancelled) {
          if (src?.startsWith("blob:")) {
            URL.revokeObjectURL(src);
          }
          return;
        }
        if (src?.startsWith("blob:")) {
          qrObjectUrlRef.current = src;
        }
        setQrSrc(src);
      } catch (error) {
        console.error("Ошибка при загрузке QR-кода:", error);
        if (!cancelled) {
          setQrError("Не удалось загрузить QR-код");
        }
      } finally {
        if (!cancelled) {
          setQrLoading(false);
        }
      }
    };

    loadQr();

    return () => {
      cancelled = true;
      revokeQrUrl();
    };
  }, [visible, employee?.id]);

  if (!employee) return null;

  // Функция для извлечения номера из названия отдела
  const extractNumber = (name) => {
    const match = name.match(/^(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : Infinity;
  };

  // Функция сортировки отделов
  const sortDepartments = (a, b) => {
    const numA = extractNumber(a.department);
    const numB = extractNumber(b.department);

    // Если оба имеют номера
    if (numA !== Infinity && numB !== Infinity) {
      return numA - numB;
    }
    // Если только первый имеет номер
    if (numA !== Infinity) {
      return -1;
    }
    // Если только второй имеет номер
    if (numB !== Infinity) {
      return 1;
    }
    // Если нет номеров, сортируем по алфавиту
    return a.department.localeCompare(b.department, "ru");
  };

  // Получаем все отделы и должности
  const departments = [];

  // Добавляем все отделы и должности из groups
  if (employee.groups) {
    employee.groups.forEach((group) => {
      if (group.roles && group.roles.length > 0) {
        group.roles.forEach((role) => {
          departments.push({
            department: group.name,
            position: role.name,
          });
        });
      }
    });
  }

  // Сортируем отделы
  departments.sort(sortDepartments);

  return (
    <Modal
      title="Информация о сотруднике"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className="employee-modal-container"
    >
      <div className="employee-modal">
        <div className="employee-modal__header">
          <div className="employee-modal__avatar">
            <img
              src={employee.image || "/assets/images/placeholder-avatar.png"}
              alt={`${employee.surname} ${employee.name}`}
              style={{
                width: "400px",
                height: "400px",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          </div>
          <div className="employee-modal__info">
            <h2 className="employee-modal__name">
              {`${employee.surname} ${employee.name} ${
                employee.patronymic || ""
              }`}
            </h2>

            <div className="employee-modal__qr">
              <label>QR-код для сохранения контакта</label>
              <div className="employee-modal__qr-content">
                {qrLoading && <Spin />}
                {!qrLoading && qrSrc && (
                  <img
                    src={qrSrc}
                    alt="QR-код контакта"
                    className="employee-modal__qr-image"
                  />
                )}
                {!qrLoading && qrError && (
                  <span className="employee-modal__qr-error">{qrError}</span>
                )}
              </div>
              {!qrLoading && qrSrc && (
                <span className="employee-modal__qr-hint">
                  Отсканируйте камерой телефона, чтобы сохранить контакт
                </span>
              )}
            </div>

            {departments.length > 0 && (
              <div className="employee-modal__positions">
                {departments.map((dep, index) => (
                  <div key={index} className="employee-modal__position-block">
                    <div className="employee-modal__detail">
                      <label>Отдел:</label>
                      <span>{dep.department}</span>
                    </div>
                    <div className="employee-modal__detail">
                      <label>Должность:</label>
                      <span>{dep.position}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="employee-modal__details">
          <div className="employee-modal__detail">
            <label>Телефон:</label>
            <span>{employee.phone_number || "Не указан"}</span>
          </div>
          <div className="employee-modal__detail">
            <label>Email:</label>
            <span>{employee.email || "Не указан"}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const OrgChartNew = () => {
  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [allEmployees, setAllEmployees] = useState([]);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const containerRef = useRef(null);
  const treeRef = useRef(null);
  const initialPositionRef = useRef(null);
  const { userData } = useContext(UserContext);
  const [allNodes, setAllNodes] = useState([]);

  // Модал "Добавить роль" (АДМС и ИК)
  const canAddRole =
    userData?.access_levels?.some(
      (level) => level.name === "АДМС" || level.name === "ИК",
    ) || false;
  const [addRoleModalVisible, setAddRoleModalVisible] = useState(false);
  const [addRoleGroupOptions, setAddRoleGroupOptions] = useState([]);
  const [addRoleName, setAddRoleName] = useState("");
  const [addRoleSelectedGroups, setAddRoleSelectedGroups] = useState([]);
  const [addRoleSubmitLoading, setAddRoleSubmitLoading] = useState(false);
  const [addRoleError, setAddRoleError] = useState(null);
  const [addRoleSuccess, setAddRoleSuccess] = useState(false);

  const processOrgData = (data) => {
    // Находим директора
    const director = data.find((item) => item.tags?.includes("director"));
    if (!director) return null;

    // Функция для извлечения числового значения из названия отделения
    const extractNumber = (name) => {
      const match = name.match(/^(\d+)(?:\.(\d+))?/);
      if (!match) return [Infinity, Infinity];
      return [
        parseInt(match[1]), // Основной номер
        match[2] ? parseInt(match[2]) : 0, // Подномер (если есть)
      ];
    };

    // Функция сортировки узлов
    const sortNodes = (nodes) => {
      return nodes.sort((a, b) => {
        // Проверяем, является ли узел отделением 7
        const isDept7A = a.name?.includes("7");
        const isDept7B = b.name?.includes("7");

        // Если один из узлов - отделение 7, ставим его первым
        if (isDept7A && !isDept7B) return -1;
        if (!isDept7A && isDept7B) return 1;

        // Для остальных узлов используем стандартную сортировку
        const [aMain, aSub] = extractNumber(a.name || "");
        const [bMain, bSub] = extractNumber(b.name || "");

        if (aMain === bMain) {
          return aSub - bSub; // Сортировка по подномеру
        }
        return aMain - bMain; // Сортировка по основному номеру
      });
    };

    // Функция для построения дерева
    const buildTree = (root) => {
      // Фильтруем дочерние элементы, исключая заместителей из начального списка
      let children = data.filter(
        (item) => item.pid === root.id && !item.tags?.includes("deputy") // Исключаем заместителей
      );

      // Сортируем дочерние элементы
      children = sortNodes(children);

      return {
        ...root,
        children: children.map((child) => buildTree(child)),
      };
    };

    return buildTree(director);
  };

  // Функция для получения центральной позиции
  const calculateCenterPosition = () => {
    if (!containerRef.current || !treeRef.current) return { x: 0, y: 0 };

    const containerRect = containerRef.current.getBoundingClientRect();
    const treeRect = treeRef.current.getBoundingClientRect();

    return {
      x: (containerRect.width - treeRect.width / zoom) / 2,
      y: (containerRect.height - treeRect.height / zoom) / 2,
    };
  };

  // Функция для сбора всех сотрудников из дерева
  const collectEmployees = (node, employees = []) => {
    if (!node) return employees;
    employees.push({
      value: node.id,
      label: `${node.name} (${node.title})`,
      data: node,
    });
    if (node.children) {
      node.children.forEach((child) => collectEmployees(child, employees));
    }
    return employees;
  };

  // Функция для поиска пути к сотруднику в дереве
  const findPathToEmployee = (node, employeeId, path = []) => {
    if (!node) return null;
    path.push(node.id);
    if (node.id === employeeId) return path;
    if (node.children) {
      for (const child of node.children) {
        const foundPath = findPathToEmployee(child, employeeId, [...path]);
        if (foundPath) return foundPath;
      }
    }
    return null;
  };

  const centerSelectedEmployee = (employeeId) => {
    if (!containerRef.current || !treeRef.current) return;

    // Добавляем небольшую задержку, чтобы дать время на раскрытие всех узлов
    setTimeout(() => {
      const employeeElement = document.querySelector(
        `[data-employee-id="${employeeId}"]`
      );
      if (!employeeElement) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const treeRect = treeRef.current.getBoundingClientRect();
      const employeeRect = employeeElement.getBoundingClientRect();

      // Получаем текущую позицию элемента относительно transform origin (0,0)
      const currentEmployeeX = (employeeRect.left - treeRect.left) / zoom;
      const currentEmployeeY = (employeeRect.top - treeRect.top) / zoom;

      // Вычисляем центр контейнера
      const containerCenterX = containerRect.width / 2;
      const containerCenterY = containerRect.height / 2;

      // Вычисляем новую позицию, учитывая размер элемента и отступы
      const newX =
        containerCenterX -
        (currentEmployeeX + employeeRect.width / (2 * zoom)) * zoom;
      const newY =
        containerCenterY -
        (currentEmployeeY + employeeRect.height / (2 * zoom)) * zoom;

      // Добавляем плавную анимацию центрирования
      setPosition({ x: newX, y: newY });
    }, 100); // Задержка в 100мс для корректного центрирования после раскрытия узлов
  };

  const handleEmployeeSelect = (option) => {
    if (!option) {
      setSelectedEmployee(null);
      return;
    }

    setSelectedEmployee(option.value);

    // Находим путь к выбранному сотруднику
    const path = findPathToEmployee(orgData, option.value);
    if (path) {
      // Раскрываем все узлы на пути к сотруднику
      const newExpandedNodes = new Set([...expandedNodes, ...path]);
      setExpandedNodes(newExpandedNodes);

      // Используем requestAnimationFrame для более плавного центрирования
      requestAnimationFrame(() => {
        setTimeout(() => {
          centerSelectedEmployee(option.value);
        }, 300);
      });
    }
  };

  // Обработчик клика по сотруднику
  const handleEmployeeClick = async (employee) => {
    try {
      console.log("Clicked employee:", employee);
      setSelectedEmployee(employee.id);

      // Получаем ID пользователя
      let userId;

      // Если это группа/департамент/секция — открываем карточку руководителя
      if (
        employee.id.startsWith("group_") ||
        employee.id.startsWith("dept_") ||
        employee.id.startsWith("section_")
      ) {
        if (!employee.leaderId) {
          throw new Error("Не удалось найти ID руководителя");
        }
        userId = employee.leaderId.toString();
      } else if (employee.id.toString().includes("_")) {
        // Если ID содержит префикс emp_
        userId = employee.id.toString().split("_")[1];
      } else {
        // Если ID не содержит префикса (например, director_id)
        userId = employee.id.toString();
      }

      const userData = await getUserById(userId);
      setSelectedEmployeeData(userData);
      setModalVisible(true);
    } catch (error) {
      console.error("Ошибка при получении данных сотрудника:", error);
    }
  };

  // Функция для раскрытия первого уровня
  const expandFirstLevel = (data) => {
    if (!data) return new Set();

    // Добавляем только ID директора в expandedNodes
    const newExpandedNodes = new Set();
    if (data.id) {
      newExpandedNodes.add(data.id);
    }

    return newExpandedNodes;
  };

  // Группы пользователя из departments (не userData.groups)
  const getUserGroups = () => {
    if (!userData?.departments) return [];
    return userData.departments.flatMap((dept) => dept.groups || []);
  };

  // Функция для поиска пути к группе или отделению в дереве
  const findPathToGroupOrDept = (node, targetId, path = []) => {
    if (!node) return null;
    const currentPath = [...path, node.id];

    if (node.id === `group_${targetId}` || node.id === `dept_${targetId}`) {
      const fullPath = [...currentPath];
      const addDescendants = (n) => {
        if (n.children) {
          n.children.forEach((child) => {
            fullPath.push(child.id);
            addDescendants(child);
          });
        }
      };
      addDescendants(node);
      return { path: fullPath, targetNodeId: node.id };
    }

    if (node.children) {
      for (const child of node.children) {
        const result = findPathToGroupOrDept(child, targetId, currentPath);
        if (result) return result;
      }
    }
    return null;
  };

  // Раскрытие одной или нескольких веток по id группы/отделения
  const expandDepartments = (targetIds) => {
    if (!orgData) return;

    const ids = (Array.isArray(targetIds) ? targetIds : [targetIds]).filter(
      Boolean,
    );
    if (!ids.length) return;

    const newExpandedNodes = new Set(expandedNodes);
    let firstTargetNodeId = null;

    ids.forEach((targetId) => {
      const result = findPathToGroupOrDept(orgData, targetId);
      if (result) {
        result.path.forEach((nodeId) => newExpandedNodes.add(nodeId));
        if (!firstTargetNodeId) {
          firstTargetNodeId = result.targetNodeId;
        }
      }
    });

    setExpandedNodes(newExpandedNodes);

    if (firstTargetNodeId) {
      setSelectedEmployee(firstTargetNodeId);
      setTimeout(() => {
        centerSelectedEmployee(firstTargetNodeId);
      }, 150);
    }
  };

  // Обработчик выбора отдела
  const handleDepartmentSelect = (option) => {
    if (!option) return;
    setSelectedDepartment(option);
    expandDepartments(option.value);
  };

  // Получаем список отделов пользователя для селекта
  const getUserDepartments = () => {
    return getUserGroups().map((group) => ({
      value: group.id,
      label: group.name,
    }));
  };

  useEffect(() => {
    const fetchOrgData = async () => {
      try {
        setLoading(true);
        const data = await getOrgChartData();
        const processedData = processOrgData(data);
        setOrgData(processedData);
        setAllNodes(data);

        // Устанавливаем начальное состояние раскрытых узлов
        setExpandedNodes(expandFirstLevel(processedData));

        // Собираем всех сотрудников для селекта
        const employees = collectEmployees(processedData);
        setAllEmployees(employees);

        // Центрируем и масштабируем для отображения всего первого уровня
        setTimeout(() => {
          const centerPosition = calculateCenterPosition();
          setPosition(centerPosition);
          initialPositionRef.current = centerPosition;

          // Устанавливаем начальный зум, чтобы было видно все отделы первого уровня
          if (containerRef.current && treeRef.current) {
            const containerWidth = containerRef.current.offsetWidth;
            const treeWidth = treeRef.current.offsetWidth;
            const containerHeight = containerRef.current.offsetHeight;
            const treeHeight = treeRef.current.offsetHeight;

            const horizontalScale = containerWidth / (treeWidth * 1.2);
            const verticalScale = containerHeight / (treeHeight * 1.2);

            // Выбираем минимальный масштаб, чтобы уместить дерево по обоим измерениям
            const newZoom = Math.min(
              Math.min(horizontalScale, verticalScale),
              1 // Ограничиваем максимальный зум единицей
            );

            setZoom(Math.max(0.5, newZoom)); // Не даем зуму быть меньше 0.5
          }
        }, 100);
      } catch (err) {
        console.error("Ошибка при загрузке данных:", err);
        setError("Не удалось загрузить организационную структуру");
      } finally {
        setLoading(false);
      }
    };

    fetchOrgData();
  }, []);

  // Обработчик изменения масштаба
  const handleZoomChange = (newZoom) => {
    if (!containerRef.current) return;

    // Получаем центр контейнера
    const containerRect = containerRef.current.getBoundingClientRect();
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;

    // Вычисляем позицию центра относительно текущего масштаба
    const currentX = (centerX - position.x) / zoom;
    const currentY = (centerY - position.y) / zoom;

    // Вычисляем новую позицию с учетом масштабирования относительно центра
    const newX = centerX - currentX * newZoom;
    const newY = centerY - currentY * newZoom;

    setZoom(newZoom);
    setPosition({ x: newX, y: newY });
  };

  // Обработчик колесика мыши
  const handleWheel = (e) => {
    e.preventDefault();
    const containerRect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - containerRect.left;
    const mouseY = e.clientY - containerRect.top;

    const currentX = (mouseX - position.x) / zoom;
    const currentY = (mouseY - position.y) / zoom;

    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.min(Math.max(0.5, zoom * delta), 3);

    const newX = mouseX - currentX * newZoom;
    const newY = mouseY - currentY * newZoom;

    setZoom(newZoom);
    setPosition({ x: newX, y: newY });
  };

  // Настройка non-passive wheel event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const wheelHandler = (e) => handleWheel(e);
    container.addEventListener("wheel", wheelHandler, { passive: false });

    return () => {
      container.removeEventListener("wheel", wheelHandler);
    };
  }, [zoom, position]); // Зависимости для useEffect

  // Обработчики перетаскивания
  const handleMouseDown = (e) => {
    if (e.button === 0) {
      e.preventDefault();
      setIsDragging(true);
      setStartPosition({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      e.preventDefault();
      setPosition({
        x: e.clientX - startPosition.x,
        y: e.clientY - startPosition.y,
      });
    }
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      e.preventDefault();
    }
    setIsDragging(false);
  };

  if (loading) {
    return <div className="orgchart-new__loading">Загрузка...</div>;
  }

  if (error) {
    return <div className="orgchart-new__error">{error}</div>;
  }

  const userGroups = getUserGroups();

  return (
    <div className="orgchart-new">
      <div className="orgchart-new__controls">
        <Select
          className="orgchart-new__search"
          options={allEmployees}
          value={
            allEmployees.find((emp) => emp.value === selectedEmployee) || null
          }
          onChange={handleEmployeeSelect}
          placeholder="Поиск сотрудника..."
          isClearable
          isSearchable
          classNamePrefix="react-select"
        />
        {userGroups.length > 1 ? (
          <Select
            className="orgchart-new__department-select"
            options={getUserDepartments()}
            value={selectedDepartment}
            onChange={handleDepartmentSelect}
            placeholder="Выберите отдел"
            isClearable
            classNamePrefix="react-select"
          />
        ) : (
          <button
            type="button"
            className="orgchart-new__my-departments-btn"
            onClick={() => expandDepartments(userGroups.map((g) => g.id))}
          >
            Мой отдел
          </button>
        )}
        <div className="orgchart-new__zoom-controls">
          <button onClick={() => handleZoomChange(Math.max(0.5, zoom - 0.1))}>
            -
          </button>
          <input
            type="range"
            min="50"
            max="300"
            value={Math.round(zoom * 100)}
            onChange={(e) => handleZoomChange(Number(e.target.value) / 100)}
            style={{ width: 100, margin: "0 10px" }}
          />
          <span>{Math.round(zoom * 100)}%</span>
          <button onClick={() => handleZoomChange(Math.min(3, zoom + 0.1))}>
            +
          </button>
        </div>
        {canAddRole && (
          <button
            type="button"
            className="orgchart-new__add-role-btn"
            onClick={() => {
              setAddRoleModalVisible(true);
              setAddRoleName("");
              setAddRoleSelectedGroups([]);
              setAddRoleError(null);
              setAddRoleSuccess(false);
              getAllGroups(null, null, true).then((data) => {
                setAddRoleGroupOptions(
                  (Array.isArray(data) ? data : []).map((g) => ({
                    value: g.id,
                    label: g.name || `Группа ${g.id}`,
                  }))
                );
              }).catch(() => setAddRoleGroupOptions([]));
            }}
          >
            Добавить роль
          </button>
        )}
      </div>
      <div
        className="orgchart-new__container"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          ref={treeRef}
          className="orgchart-new__tree"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
          }}
        >
          {orgData && (
            <TreeNode
              node={orgData}
              onEmployeeClick={handleEmployeeClick}
              expandedNodes={expandedNodes}
              setExpandedNodes={setExpandedNodes}
              selectedEmployee={selectedEmployee}
              allNodes={allNodes}
            />
          )}
        </div>
      </div>
      <EmployeeModal
        visible={modalVisible}
        employee={selectedEmployeeData}
        onClose={() => {
          setModalVisible(false);
          setSelectedEmployeeData(null);
        }}
      />
      <Modal
        className="orgchart-new__add-role-modal"
        title="Добавить роль"
        open={addRoleModalVisible}
        onCancel={() => setAddRoleModalVisible(false)}
        onOk={async () => {
          const name = (addRoleName || "").trim();
          if (!name) {
            setAddRoleError("Укажите название роли");
            return;
          }
          if (!addRoleSelectedGroups?.length) {
            setAddRoleError("Выберите хотя бы одну группу");
            return;
          }
          setAddRoleError(null);
          setAddRoleSubmitLoading(true);
          try {
            await createRole({
              name,
              group_ids: addRoleSelectedGroups.map((o) => o.value),
            });
            setAddRoleSuccess(true);
            message.success("Роль успешно создана");
            setTimeout(() => {
              setAddRoleModalVisible(false);
            }, 1500);
          } catch (err) {
            setAddRoleError(err.message || "Ошибка при создании роли");
          } finally {
            setAddRoleSubmitLoading(false);
          }
        }}
        okText="Создать"
        cancelText="Отмена"
        confirmLoading={addRoleSubmitLoading}
        destroyOnClose
        width={480}
      >
        <div className="orgchart-new__add-role-form">
          {addRoleSuccess && (
            <p className="orgchart-new__add-role-success">
              Роль успешно создана
            </p>
          )}
          {addRoleError && (
            <p className="orgchart-new__add-role-error">{addRoleError}</p>
          )}
          <div className="orgchart-new__add-role-field">
            <label>Группы</label>
            <Select
              isMulti
              options={addRoleGroupOptions}
              value={addRoleSelectedGroups}
              onChange={setAddRoleSelectedGroups}
              placeholder="Выберите группы"
              noOptionsMessage={() => "Группы не найдены"}
              styles={selectStyles}
            />
          </div>
          <div className="orgchart-new__add-role-field">
            <label>Название роли</label>
            <input
              type="text"
              value={addRoleName}
              onChange={(e) => setAddRoleName(e.target.value)}
              placeholder="Введите название роли"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrgChartNew;
